import React from "react";

/**
 * GuidedTopicJourney — guided wizard container.
 *
 * Renders the progression stepper AND the active step's content inside one
 * shared-surface card (DESIGN.md §4/§5). Only the step matching `viewStep` is
 * shown (the host gates its children); tapping an unlocked step switches view.
 *
 * It does NOT own any completion logic. Advancement happens automatically
 * inside the topic page (vocab expansion + Continue, scenario Continue, audio
 * onended × 3, role-play sessionComplete, quiz pass).
 *
 * Props:
 *   progression        — backend payload (see topic_progression.progression_payload):
 *                        { steps:[{key,title,order,completed,locked,current}],
 *                          listening_play_count, topic_completed, current_step, … }
 *   viewStep           — key of the step whose content is currently displayed.
 *   onSelectStep(key)  — called when the user taps a navigable step.
 *                        Every step is tappable (preview/review) for all users —
 *                        completion order is still enforced by the backend and by
 *                        each step panel's own launch lock, so previewing an
 *                        upcoming step never lets it be completed out of order.
 *   action             — optional descriptor for the primary CTA shown beneath
 *                        the content: { label, onClick, disabled?, hint? }.
 *   children           — the active step's content, gated by the host.
 *
 * When there is no progression, the stepper is skipped and children render
 * as-is (preserves topics that don't use guided progression).
 */

function CheckIcon({ className = "w-4 h-4" }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M5 10.5l3.2 3.2L15 7"
        stroke="currentColor"
        strokeWidth={2.2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// DESIGN §5 step-circle states.
const CIRCLE_STYLE = {
  done: { background: "var(--gold)", borderColor: "var(--gold)", color: "var(--ink)" },
  current: {
    background: "var(--ink)",
    borderColor: "var(--gold)",
    color: "var(--gold-bright)",
    boxShadow: "0 0 0 4px rgba(201,162,39,0.15)",
  },
  upcoming: { background: "var(--ink)", borderColor: "var(--gold-dim)", color: "var(--parchment-dim)" },
};
const NAME_STYLE = {
  done: { color: "var(--parchment)" },
  current: { color: "var(--gold-bright)" },
  upcoming: { color: "var(--parchment-dim)" },
};

export default function GuidedTopicJourney({
  progression,
  viewStep,
  onSelectStep,
  action,
  children,
}) {
  if (!progression) return children ? <>{children}</> : null;

  const steps = progression.steps || [];
  const completedCount = steps.filter((s) => s.completed).length;
  const total = Math.max(steps.length, 1);
  const pct = Math.round((completedCount / total) * 100);

  const currentIdx = (() => {
    const cur = steps.find((s) => s.current);
    if (cur) return cur.order;
    return progression.topic_completed ? steps.length : 1;
  })();

  const playCount = Math.min(progression.listening_play_count || 0, 3);

  // Every step is navigable (preview/review) for all users; the backend and each
  // step panel's own launch lock enforce that steps still complete in order.
  const isInteractive = () => true;

  // done | current | upcoming — the viewed step reads as "current".
  const stepState = (step) => {
    if (step.completed) return "done";
    if (step.key === viewStep || step.current) return "current";
    return "upcoming";
  };

  const statusText = (step) => {
    if (step.key === "listening" && !step.completed) return `${playCount}/3 plays`;
    if (step.completed) return "Completed";
    if (step.current || step.key === viewStep) return "In progress";
    return isInteractive(step) ? "Preview" : "Locked";
  };

  const handleSelect = (step) => {
    if (!isInteractive(step) || !onSelectStep) return;
    onSelectStep(step.key);
  };

  const activeStep = steps.find((s) => s.key === viewStep);

  // Shared stepper renderer (desktop lg / mobile sm differ only in sizing).
  // The gold ring marks the CURRENT step; the framed square marks the SELECTED
  // (viewed) step — but only when it differs from current, so they never stack.
  const renderStepper = (compact) => (
    <ol className={`flex items-start list-none m-0 ${compact ? "sm:hidden px-2 pt-3 pb-1" : "hidden sm:flex px-2 pt-4 pb-2"}`}>
      {steps.map((step, i) => {
        const interactive = isInteractive(step);
        const state = stepState(step);
        const framed = step.key === viewStep && !step.current;
        const Tag = interactive ? "button" : "div";
        const size = compact ? "w-9 h-9 text-base" : "w-11 h-11 text-lg";
        return (
          <li key={step.key} className="flex-1 flex flex-col items-center relative min-w-0">
            {/* hairline connector from the previous circle */}
            {i > 0 && (
              <span
                aria-hidden="true"
                className={`absolute ${compact ? "top-[18px]" : "top-[22px]"} right-1/2 left-[-50%] h-px`}
                style={{ background: "rgba(201,162,39,0.22)" }}
              />
            )}
            {/* faint framed outline around the SELECTED (viewed) step only */}
            {framed && (
              <span
                aria-hidden="true"
                className="absolute rounded-xl"
                style={{ inset: "-6px -6px -8px", border: "1px solid var(--panel-line)", zIndex: 0 }}
              />
            )}
            <Tag
              type={interactive ? "button" : undefined}
              onClick={interactive ? () => handleSelect(step) : undefined}
              disabled={!interactive}
              aria-current={step.key === viewStep ? "step" : undefined}
              aria-disabled={!interactive || undefined}
              title={step.title}
              className={`relative z-10 flex flex-col items-center gap-2 w-full px-1 ${
                interactive ? "cursor-pointer" : "cursor-not-allowed"
              }`}
            >
              <span
                className={`flex items-center justify-center rounded-full border-2 font-cormorant font-semibold transition-all ${size}`}
                style={CIRCLE_STYLE[state]}
              >
                {step.order}
              </span>
              {!compact && (
                <>
                  <span
                    className="font-cormorant font-semibold text-base text-center leading-tight truncate max-w-full"
                    style={NAME_STYLE[(step.current || step.key === viewStep) ? "current" : state]}
                  >
                    {step.title}
                  </span>
                  <span className="text-[0.76rem] leading-none" style={{ color: "var(--parchment-dim)" }}>
                    {statusText(step)}
                  </span>
                </>
              )}
              {compact && (
                <span
                  className="font-cormorant font-semibold text-[0.7rem] text-center leading-tight truncate max-w-full"
                  style={NAME_STYLE[(step.current || step.key === viewStep) ? "current" : state]}
                >
                  {step.title}
                </span>
              )}
            </Tag>
          </li>
        );
      })}
    </ol>
  );

  return (
    <>
    {/* ── Journey card: the stepper only (separate from the step content) ── */}
    <section aria-label="Guided topic journey" className="dark-scope surface-glass mb-[22px] pb-4" style={{ borderRadius: 14 }}>
      {/* Header */}
      <div className="flex items-start justify-between gap-3 px-5 pt-4 pb-1">
        <div className="min-w-0">
          <h3 className="font-cormorant font-semibold text-xl leading-tight" style={{ color: "var(--parchment)" }}>
            Guided Journey
          </h3>
          <p className="text-sm leading-tight mt-0.5" style={{ color: "var(--parchment-dim)" }}>
            Complete each step in order to finish this topic.
          </p>
        </div>
        <span
          className="shrink-0 inline-flex items-center gap-1.5 rounded-2xl px-3 py-1 text-[0.78rem] whitespace-nowrap"
          style={{ color: "var(--gold-bright)", border: "1px solid var(--panel-line)" }}
        >
          {progression.topic_completed ? (
            <>
              <CheckIcon className="w-3.5 h-3.5" />
              Completed
            </>
          ) : (
            <>Step {currentIdx} / {steps.length}</>
          )}
        </span>
      </div>

      {/* Gold progress bar */}
      <div className="px-5 pt-3.5 pb-1">
        <div className="progress-track" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label="Topic completion">
          <div className="progress-fill" style={{ width: `${pct}%` }} />
        </div>
      </div>

      {/* Stepper (desktop + mobile) */}
      {renderStepper(false)}
      {renderStepper(true)}
    </section>

    {/* ── Section head (outside the journey card) ── */}
    {activeStep && (
      <div className="dark-scope flex items-center gap-3 mb-4">
        <span
          className="inline-flex items-center justify-center rounded-full text-[0.85rem] font-bold"
          style={{ width: 26, height: 26, background: "var(--gold)", color: "var(--ink)" }}
        >
          {activeStep.order}
        </span>
        <h2 className="font-cormorant font-semibold text-[1.4rem] m-0" style={{ color: "var(--parchment)" }}>
          {activeStep.title}
        </h2>
        {activeStep.completed && (
          <span className="inline-flex items-center gap-1.5 text-[0.8rem]" style={{ color: "var(--teal)" }}>
            <CheckIcon className="w-3.5 h-3.5" /> Completed
          </span>
        )}
      </div>
    )}

    {/* ── Active step content (its own card, e.g. the scenario surface) ── */}
    {children}

    {/* ── Current-step action band ── */}
    {action && !progression.topic_completed && (
      <div className="dark-scope surface-glass flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2.5 mt-4 px-5 py-3 rounded-xl">
        {action.hint && <p className="text-xs leading-snug" style={{ color: "var(--parchment-dim)" }}>{action.hint}</p>}
        <button
          type="button"
          onClick={action.onClick}
          disabled={action.disabled}
          className="shrink-0 w-full sm:w-auto inline-flex items-center justify-center gap-1.5 px-4 py-2.5 min-h-[44px] text-sm font-semibold rounded-lg transition-[filter] hover:brightness-105 disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ background: "var(--gold)", color: "var(--ink)" }}
        >
          {action.label}
        </button>
      </div>
    )}

    {progression.topic_completed && (
      <div
        className="flex items-center gap-2 mt-4 px-5 py-3 rounded-xl"
        style={{ border: "1px solid var(--panel-line)", background: "var(--gold-bg)" }}
      >
        <span className="text-lg leading-none" aria-hidden="true">🎉</span>
        <p className="text-xs font-medium" style={{ color: "var(--parchment)" }}>
          Topic complete — every step done. Tap any step above to review it.
        </p>
      </div>
    )}
    </>
  );
}
